/**
 * Invoice payment / approval status workflow.
 *
 * Stored values are stable English keys; display labels are derived.
 * "Overdue" is NOT a stored status — it is derived from dueDate + status
 * so it never needs to be kept in sync manually.
 */
export type InvoiceStatus = 'open' | 'in_review' | 'approved' | 'paid'

export const INVOICE_STATUSES: InvoiceStatus[] = ['open', 'in_review', 'approved', 'paid']

export const STATUS_LABELS: Record<InvoiceStatus, string> = {
  open: 'Open',
  in_review: 'In Review',
  approved: 'Approved',
  paid: 'Paid',
}

/** MUI Chip colors per status */
export const STATUS_COLORS: Record<InvoiceStatus, 'default' | 'warning' | 'success' | 'info'> = {
  open: 'default',
  in_review: 'warning',
  approved: 'success',
  paid: 'info',
}

export const STATUS_OPTIONS = INVOICE_STATUSES.map((value) => ({
  value,
  label: STATUS_LABELS[value],
}))

/** Coalesce an unknown/legacy status value to a valid status (default: open). */
export function normalizeStatus(status: string | null | undefined): InvoiceStatus {
  if (status === 'in_review' || status === 'approved' || status === 'paid') return status
  return 'open'
}

/** Parse a date string that may be ISO (YYYY-MM-DD) or German (DD.MM.YYYY). */
export function parseLooseDate(value: string | null | undefined): Date | null {
  if (!value) return null
  const trimmed = value.trim()
  if (!trimmed) return null

  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed)
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]))

  const de = /^(\d{1,2})\.(\d{1,2})\.(\d{4})/.exec(trimmed)
  if (de) return new Date(Number(de[3]), Number(de[2]) - 1, Number(de[1]))

  const parsed = new Date(trimmed)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/**
 * Whether a status still needs the reviewer's attention (open or in review).
 * Approved invoices are awaiting payment (finance's job) and paid ones are done,
 * so neither counts toward overdue / due-soon reminders.
 */
export function needsAttention(status: string | null | undefined): boolean {
  const s = normalizeStatus(status)
  return s === 'open' || s === 'in_review'
}

/**
 * An invoice is overdue when it still needs attention and its due date is past.
 * Comparison is date-only (ignores time of day).
 */
export function isOverdue(
  dueDate: string | null | undefined,
  status: string | null | undefined,
  documentType?: string | null,
  now: Date = new Date(),
): boolean {
  // Only normal invoices can be overdue — credit notes / cancellations are not payables.
  if (documentType && documentType !== 'invoice') return false
  if (!needsAttention(status)) return false
  const due = parseLooseDate(dueDate)
  if (!due) return false
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return due.getTime() < today.getTime()
}

/** Today's date as an ISO YYYY-MM-DD string (local time). */
export function todayIso(now: Date = new Date()): string {
  const mm = String(now.getMonth() + 1).padStart(2, '0')
  const dd = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${mm}-${dd}`
}

/** How many invoices a due date is "due soon" within. */
export const DUE_SOON_DAYS = 7

/**
 * Whole days until the due date (date-only). Negative = overdue, 0 = today,
 * null = no/invalid due date.
 */
export function daysUntilDue(dueDate: string | null | undefined, now: Date = new Date()): number | null {
  const due = parseLooseDate(dueDate)
  if (!due) return null
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const target = new Date(due.getFullYear(), due.getMonth(), due.getDate()).getTime()
  return Math.round((target - today) / 86_400_000)
}

/**
 * Due within the next `within` days (and not yet overdue or paid).
 * Overdue invoices are handled by isOverdue(), not here.
 */
export function isDueSoon(
  dueDate: string | null | undefined,
  status: string | null | undefined,
  documentType?: string | null,
  now: Date = new Date(),
  within: number = DUE_SOON_DAYS,
): boolean {
  if (documentType && documentType !== 'invoice') return false
  if (!needsAttention(status)) return false
  const d = daysUntilDue(dueDate, now)
  return d !== null && d >= 0 && d <= within
}
